import * as THREE from 'three';
import { DinoActor, spearHit, type DinoSnap, type FaunaSense } from '../dinos/DinoActor';
import {
  COMPY_KIT,
  DEINONYCHUS_KIT,
  DILOPH_KIT,
  PARA_KIT,
  PTERA_KIT,
  RAPTOR_KIT,
  type Aabb2,
  type DinoKit,
} from '../dinos/kits';
import { setPrompt } from '../ui/Prompt';
import type { Xyz } from '../interact/Interact';
import { isCoastWater, NavGrid, WRECK_BLOCKERS } from './NavGrid';

export type DinoHook = {
  dinos: DinoSnap[];
  blind: number;
  prompt?: string;
};

const CABIN = { x: -8, z: -22 };
const COMPY_BOUNDS: Aabb2 = { minX: -14, maxX: -2, minZ: -28, maxZ: -16 };
const DILOPH_BOUNDS: Aabb2 = { minX: 6, maxX: 18, minZ: -38, maxZ: -22 };

/** Pack of 5 around the cabin / luggage scatter. */
const COMPY_SPAWNS: { x: number; z: number }[] = [
  { x: CABIN.x + 1.8, z: CABIN.z + 1.6 },
  { x: CABIN.x - 1.7, z: CABIN.z - 1.8 },
  { x: CABIN.x + 2.6, z: CABIN.z - 1.1 },
  { x: CABIN.x - 2.4, z: CABIN.z + 1.2 },
  { x: CABIN.x - 0.4, z: CABIN.z - 3.1 },
];

const DILOPH_SPAWN = { x: 12, z: -30 };

const RAPTOR_BOUNDS: Aabb2 = { minX: -18, maxX: 12, minZ: -118, maxZ: -82 };
/** Pack of 3 around Deep Jungle z=-100, north of Station Meridian. */
const RAPTOR_SPAWNS: { x: number; z: number }[] = [
  { x: -8, z: -98 },
  { x: -2, z: -100 },
  { x: 4, z: -102 },
];

const PTERA_BOUNDS: Aabb2 = { minX: 18, maxX: 32, minZ: -20, maxZ: 6 };
const PTERA_SPAWN = { x: 28, z: -8 };

const PARA_BOUNDS: Aabb2 = { minX: -24, maxX: 4, minZ: -100, maxZ: -82 };
const PARA_SPAWN = { x: -14, z: -90 };

const DEINO_BOUNDS: Aabb2 = { minX: 6, maxX: 18, minZ: -92, maxZ: -78 };
const DEINO_SPAWN = { x: 10, z: -84 };

export class SpawnDirector {
  readonly actors: DinoActor[] = [];
  /** Single grid pathfinder — fauna must not invent a second A*. */
  readonly nav = new NavGrid({ blockers: WRECK_BLOCKERS, water: isCoastWater });
  prompt = '';
  /** SEAL / Pulse-still: no new fauna. Existing actors keep wandering. */
  paused = false;
  private blindLeft = 0;
  private readonly root: THREE.Group;
  constructor(scene: THREE.Scene) {
    this.root = new THREE.Group();
    this.root.name = 'CC-FAUNA';
    this.root.userData.id = 'CC-FAUNA';
    scene.add(this.root);
    this.spawnCoast();
    this.spawnRaptors();
    this.spawnJungleHerd();
  }

  get blind(): number {
    return this.blindLeft;
  }

  /** Pulse-like extra spawn. Returns null while paused (SEAL). */
  spawn(id: string, kit: DinoKit, x: number, z: number, bounds: Aabb2, floorY = 0): DinoActor | null {
    if (this.paused) {
      return null;
    }
    const dino = new DinoActor(id, kit, x, z, bounds, floorY);
    this.actors.push(dino);
    this.root.add(dino.mesh);
    return dino;
  }

  /**
   * Wander / flee / alert / combat. XZ AABB wander; nav is queried, not a second A*.
   * Returns true if a spear swing consumed the interact edge.
   */
  tick(dt: number, playerPos: Xyz, sense?: FaunaSense): boolean {
    this.nav.syncWindow(playerPos.x, playerPos.z);
    this.blindLeft = Math.max(0, this.blindLeft - dt);
    const inner = this.wrap(sense);
    let used = false;
    if (sense?.interact && sense.held === 'spear') {
      used = this.trySpear(sense.camera, playerPos);
    }
    this.assignRaptorPack();
    for (const dino of this.actors) {
      dino.tick(dt, playerPos, inner);
    }
    this.refreshPrompt(playerPos, sense);
    return used;
  }

  publish(hook: DinoHook): void {
    hook.dinos = this.actors.map((dino) => dino.snap());
    hook.blind = this.blindLeft;
    if (this.prompt && !hook.prompt) {
      hook.prompt = this.prompt;
      setPrompt(this.prompt);
    }
  }

  private spawnCoast(): void {
    COMPY_SPAWNS.forEach((spot, i) => {
      this.spawn(`CC-COMPY-0${i + 1}`, COMPY_KIT, spot.x, spot.z, COMPY_BOUNDS);
    });
    this.spawn('CC-DILOPH', DILOPH_KIT, DILOPH_SPAWN.x, DILOPH_SPAWN.z, DILOPH_BOUNDS);
    this.spawn('CC-PTERA', PTERA_KIT, PTERA_SPAWN.x, PTERA_SPAWN.z, PTERA_BOUNDS);
  }

  private spawnJungleHerd(): void {
    this.spawn('DJ-PARA', PARA_KIT, PARA_SPAWN.x, PARA_SPAWN.z, PARA_BOUNDS);
    const deino = this.spawn('DJ-DEINO', DEINONYCHUS_KIT, DEINO_SPAWN.x, DEINO_SPAWN.z, DEINO_BOUNDS);
    if (deino) {
      deino.packRole = 'rush';
    }
  }

  private spawnRaptors(): void {
    RAPTOR_SPAWNS.forEach((spot, i) => {
      const dino = this.spawn(`DJ-RAPTOR-0${i + 1}`, RAPTOR_KIT, spot.x, spot.z, RAPTOR_BOUNDS);
      if (!dino) {
        return;
      }
      dino.packRole = i === 1 ? 'rush' : 'circle';
      dino.packSign = i === 0 ? -1 : 1;
    });
  }

  private assignRaptorPack(): void {
    const pack = this.actors.filter((dino) => dino.kind === 'raptor' && dino.live);
    if (pack.length === 0 || pack.some((dino) => dino.packRole === 'rush')) {
      return;
    }
    const next = pack[0];
    if (next) {
      next.packRole = 'rush';
    }
  }

  private trySpear(camera: THREE.Camera, player: Xyz): boolean {
    let best: DinoActor | null = null;
    let bestDist = Infinity;
    for (const dino of this.actors) {
      if (!dino.inSpearCone(camera, player)) {
        continue;
      }
      const dist = Math.hypot(dino.x - player.x, dino.z - player.z);
      if (dist < bestDist) {
        best = dino;
        bestDist = dist;
      }
    }
    if (!best) {
      return false;
    }
    spearHit(best);
    return true;
  }

  private refreshPrompt(player: Xyz, sense?: FaunaSense): void {
    this.prompt = '';
    if (!sense || sense.held !== 'spear') {
      return;
    }
    for (const dino of this.actors) {
      if (dino.inSpearCone(sense.camera, player)) {
        this.prompt = 'Click — Spear';
        return;
      }
    }
  }

  private wrap(sense?: FaunaSense): FaunaSense | undefined {
    if (!sense) {
      return undefined;
    }
    return {
      ...sense,
      blind: (seconds: number) => {
        this.blindLeft = Math.max(this.blindLeft, seconds);
        sense.blind?.(seconds);
      },
    };
  }
}
